import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield } from "lucide-react";
import logo1 from "../../../Assets/ClientsLogo/facor-logo.png";
import logo2 from "../../../Assets/ClientsLogo/orsac.jpeg";
import logo3 from "../../../Assets/ClientsLogo/rail.webp";
import logo4 from "../../../Assets/ClientsLogo/Vedanta.jpg";
import logo5 from "../../../Assets/ClientsLogo/IDCO.jpg";
import logo6 from "../../../Assets/ClientsLogo/SAIL_Logo.png";
import logo7 from "../../../Assets/ClientsLogo/optcl.png";
import logo8 from "../../../Assets/ClientsLogo/Adani_logo_2012.png";
import logo9 from "../../../Assets/ClientsLogo/L&T.png";
import logo10 from "../../../Assets/ClientsLogo/shree.png";
import logo11 from "../../../Assets/ClientsLogo/shreehari.png";
import logo12 from "../../../Assets/ClientsLogo/watco-logo.png";
import logo13 from "../../../Assets/ClientsLogo/OMC.avif";
import logo14 from "../../../Assets/ClientsLogo/POWERGRID.png";
import logo15 from "../../../Assets/ClientsLogo/Dalmia.jpg";
import logo16 from "../../../Assets/ClientsLogo/goo.png";
import logo17 from "../../../Assets/ClientsLogo/MAYFAIR.jpg";
import logo18 from "../../../Assets/ClientsLogo/kh.jpeg";
import logo19 from "../../../Assets/ClientsLogo/Trident.jpg";
import logo20 from "../../../Assets/ClientsLogo/sparc.png";

const ClientLogos = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  // Client Logos
  const clients = [
    {
      name: "Ferro Alloy Corporation Limited",
      logo: logo1,
    },
    {
      name: "ORSAC",
      logo: logo2,
    },
    {
      name: "East Cost Railway",
      logo: logo3,
    },
    {
      name: "Vedanta",
      logo: logo4,
    },
    {
      name: "IDCO",
      logo: logo5,
    },
    {
      name: "SAIL",
      logo: logo6,
    },
    {
      name: "OPTCL",
      logo: logo7,
    },
    {
      name: "Adani",
      logo: logo8,
    },


    {
      name: "L & T",
      logo: logo9,
    },
    {
      name: "Shree Metaliks",
      logo: logo10,
    },
    {
      name: "Shree Hari India",
      logo: logo11,
    },
    {
      name: "WATCO",
      logo: logo12,
    },
    {
      name: "OMC",
      logo: logo13,
    },
    {
      name: "Powergrid",
      logo: logo14,
    },
    {
      name: "Dalmia",
      logo: logo15,
    },
    {
      name: "Govt of Odisha",
      logo: logo16,
    },
    {
      name: "Mayfair",
      logo: logo17,
    },
    {
      name: "Kalinga Hospital",
      logo: logo18,
    },
    {
      name: "Trident Hilton",
      logo: logo19,
    },
    {
      name: "Sparc India",
      logo: logo20,
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-6 lg:py-12 bg-white overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 bg-brandBlue rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-brandGreen rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center max-w-4xl mx-auto mb-10 lg:mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 border border-brandBlue/20 mb-4"
          >
            <Shield className="w-4 h-4 text-brandBlue mr-2" />
            <span className="text-sm font-semibold text-brandText">
              Trusted By Industry Leaders
            </span>
          </motion.div>

          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-4 font-lexend leading-tight">
            Our{" "}
            <span className="bg-gradient-to-r from-brandBlue to-brandGreen bg-clip-text text-transparent">
              Clients
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-secondaryTextColor leading-relaxed">
            We're proud to partner with leading organizations across various
            sectors, delivering exceptional results and building lasting
            relationships.
          </p>
        </motion.div>

        {/* Client Logos Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8"
        >
          {clients.map((client, index) => (
            <motion.div
              key={client.name}
              variants={itemVariants}
              className="group relative p-6 lg:p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:scale-105 hover:shadow-xl"
            >
              {/* Logo Container */}
              <div className="w-full h-20 flex items-center justify-center">
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* Logo Image */}
                  <motion.img
                    src={client.logo}
                    alt={client.name}
                    className="max-h-12 lg:max-h-24 w-auto object-contain group-hover:opacity-100 transition-all duration-500"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  />

                  {/* Hover Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brandBlue/5 to-brandGreen/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>

              {/* Client Name - Hidden by default, shows on hover */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileHover={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-full px-4"
              >
                <p className="text-center text-secondaryTextColor text-xs font-medium bg-white/90 backdrop-blur-sm py-1 px-3 rounded-full border border-gray-200 shadow-sm">
                  {client.name}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="grid grid-cols-3 gap-8 mt-16 pt-12 border-t border-gray-200 max-w-2xl mx-auto"
        >
          {[
            { number: "40+", label: "Happy Clients" },
            { number: "98%", label: "Retention Rate" },
            { number: "80+", label: "Projects Delivered" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl font-bold text-brandBlue mb-2">
                {stat.number}
              </div>
              <div className="text-secondaryTextColor text-sm font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ClientLogos;
